import {
  put, takeLatest, takeEvery, select, all,
} from 'redux-saga/effects';
import history from 'app/routes/history';
import _ from 'lodash';
import {
  TeammoApi,
  normalize,
  compilePaginationObject,
  getStoredAdmin,
} from 'app/utils/redux/helpers';
import {
  organisationSchema,
  organisationListSchema,
} from 'app/utils/redux/schemas';
import { IStoredAdmin } from 'app/types/common';
import IApplicationState from 'app/types/state';
import { handleError } from 'app/utils/handlers/handleError';
import { loadingStart, loadingFinish } from 'app/ducks/loading/actions';
import { sagaActions } from 'app/ducks/organisations/actions';
import { getCompetition } from 'app/ducks/competitions/actions';
import { OrganisationsActionTypes as types } from 'app/ducks/organisations/types';

function* createOrganisationSaga(action): any {
  yield put(sagaActions.createOrganisationPending(action.payload));
  const [error, response] = yield (yield TeammoApi(
    yield select(),
  )).organisations.create(action.payload);

  if (response) {
    const normalizedResponse = normalize(response, organisationSchema);

    yield put(sagaActions.createOrganisationSuccess(normalizedResponse));
  } else {
    yield put(
      handleError(
        error,
        sagaActions.createOrganisationFailed(_.isEmpty(error) ? {} : error.response.data.errors),
      ),
    );
  }
}

function* updateOrganisationSaga(action): any {
  yield put(sagaActions.updateOrganisationPending(action.payload));
  const [error, response] = yield (yield TeammoApi(
    yield select(),
  )).organisations.update(action.payload);

  if (response) {
    const normalizedResponse = normalize(response, organisationSchema);

    yield put(sagaActions.updateOrganisationSuccess(normalizedResponse));
  } else {
    yield put(
      handleError(
        error,
        sagaActions.updateOrganisationFailed(_.isEmpty(error) ? {} : error.response.data.errors),
      ),
    );
  }
}

function* getOrganisationSaga(action): any {
  yield put(sagaActions.getOrganisationPending());
  const [error, response] = yield (yield TeammoApi(
    yield select(),
  )).organisations.show(action.payload);

  if (response) {
    const normalizedResponse = normalize(response, organisationSchema);
    const organisation = normalizedResponse.entities.organisations[normalizedResponse.result];
    yield put(
      sagaActions.getOrganisationSuccess(
        normalizedResponse,
      ),
    );

    const storedUser: IStoredAdmin = getStoredAdmin();
    const storedCompetitionId = storedUser.currentCompetitionId;
    const stateCompetitionId = yield select(
      (state: IApplicationState) => state.competitions.currentCompetitionId,
    );
    let currentCompetitionId = storedCompetitionId || stateCompetitionId;

    if (
      storedCompetitionId === undefined
      && stateCompetitionId === undefined
      && !_.isEmpty(organisation.competitions)
    ) {
      currentCompetitionId = organisation.competitions[0].id;
    }
    if (currentCompetitionId === undefined) {
      history.push('/competitions');
      return true;
    }
    yield put(yield getCompetition(currentCompetitionId));
    return organisation;
  }
  yield put(
    handleError(
      error,
      sagaActions.getOrganisationFailed(_.isEmpty(error) ? {} : error.response.data.errors),
    ),
  );
  return null;
}

function* getAllOrganisationsSaga(action): any {
  yield put(
    sagaActions.getAllOrganisationsPending(
      action.payload.compile ? action.payload.pagination : {},
    ),
  );
  yield put(loadingStart());

  const paginationObject = action.payload.compile
    ? compilePaginationObject(
      yield select((state) => state.organisations.pagination),
    )
    : action.payload.pagination;

  const [error, response] = yield (yield TeammoApi(
    yield select(),
  )).organisations.list(paginationObject);

  if (response) {
    const normalizedResponse = normalize(response, organisationListSchema);

    yield put(
      sagaActions.getAllOrganisationsSuccess(normalizedResponse, response.meta),
    );
    yield put(loadingFinish());
  } else {
    yield put(
      error,
      sagaActions.getAllOrganisationsFailed(_.isEmpty(error) ? {} : error.response.data.errors),
    );
  }
}

/* ********************************* SAGA WATCHERS ********************************* */

function* createOrganisationSagaWatcher(): any {
  yield takeLatest(types.CREATE_ORGANISATION_REQUESTED, createOrganisationSaga);
}

function* updateOrganisationSagaWatcher(): any {
  yield takeLatest(types.UPDATE_ORGANISATION_REQUESTED, updateOrganisationSaga);
}

function* getOrganisationSagaWatcher(): any {
  yield takeEvery(types.GET_ORGANISATION_REQUESTED, getOrganisationSaga);
}

function* getAllOrganisationsSagaWatcher(): any {
  yield takeLatest(
    types.GET_ALL_ORGANISATIONS_REQUESTED,
    getAllOrganisationsSaga,
  );
}

export default function* root(): any {
  yield all([
    createOrganisationSagaWatcher(),
    updateOrganisationSagaWatcher(),
    getOrganisationSagaWatcher(),
    getAllOrganisationsSagaWatcher(),
  ]);
}
